import { useState, useEffect } from 'react';
import { Box, Button, Collapse, Dialog, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DialogActionRoot, DialogContentRoot, DialogTitleStyled } from './popupStyles';
import { isPromise } from '../../helpers/promise';
import { ButtonLoadingIndicator } from '../../controls';

interface PopupProps {
  open: boolean;
  onClose: () => void;
  dialogTitle: string;
  dialogContent?: React.JSX.Element;
  dialogActions?: React.JSX.Element;
  closeButton?: boolean;
  isButtonDisabled?: boolean;
  onAction?: () => void | Promise<unknown>;
  buttonContent?: string;
  isSuccess?: boolean;
  isFailure?: boolean;
  successText?: string;
  failureText?: string;
  submitOnEnter?: boolean;
}

export function Popup(props: PopupProps) {
  var {
    open,
    onClose,
    dialogTitle,
    dialogContent,
    dialogActions,
    closeButton = true,
    isButtonDisabled,
    onAction,
    buttonContent,
    isSuccess = false,
    successText,
    isFailure = false,
    failureText,
    submitOnEnter = false,
  } = props;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isSuccess || isFailure) {
      setIsLoading(false);
    }
  }, [isSuccess, isFailure]);

  const onClick = async () => {
    if (!onAction) {
      return;
    }

    try {
      setIsLoading(true);
      const response = onAction();

      if (isPromise(response)) {
        await response;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const button = () => {
    return (
      <Button
        sx={{
          width: '100%',
          backgroundColor: 'button.secondary.background',
          color: 'button.primary.color',
          height: '50px',
        }}
        onClick={submitOnEnter ? undefined : onClick}
        type={submitOnEnter ? 'submit' : 'button'}
        form={submitOnEnter ? 'popupForm' : undefined}
        disabled={isButtonDisabled || isLoading}
        loading={isLoading}
        loadingIndicator={<ButtonLoadingIndicator />}
        variant="contained"
      >
        <Box>{buttonContent}</Box>
      </Button>
    );
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isButtonDisabled && !isLoading) {
      await onClick();
    }
  };

  const content = submitOnEnter ? (
    <form id="popupForm" onSubmit={handleSubmit}>
      {dialogContent}
    </form>
  ) : (
    dialogContent
  );

  return (
    <Dialog open={open} onClose={(_, reason) => reason !== 'backdropClick' && onClose()} fullWidth maxWidth="sm">
      <DialogTitleStyled>{dialogTitle}</DialogTitleStyled>
      {closeButton && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <CloseIcon htmlColor="black" />
        </IconButton>
      )}
      <DialogContentRoot>
        {content}
        <Collapse in={isSuccess && !!successText}>
          <Typography
            sx={{
              textAlign: 'center',
              color: 'success.light',
              mt: '5px',
            }}
          >
            {successText}
          </Typography>
        </Collapse>
        <Collapse in={isFailure && !!failureText}>
          <Typography sx={{ textAlign: 'center', color: 'error.main', mt: '5px' }}>{failureText}</Typography>
        </Collapse>
      </DialogContentRoot>
      <DialogActionRoot>{dialogActions ? dialogActions : button()}</DialogActionRoot>
    </Dialog>
  );
}
